import TabBar from '@/components/TabBar'
import { useAuthStore } from '@/stores/authStore'
import { useTabStore } from '@/stores/tabStore'
import {
  AppstoreOutlined,
  CloudUploadOutlined,
  FileTextOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MobileOutlined,
  RocketOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Layout as AntLayout, Avatar, Button, Dropdown, Menu, theme } from 'antd'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLayoutStore } from '../../stores/layoutStore'
import './index.scss'

const { Header, Sider, Content } = AntLayout

interface LayoutProps {
  children: React.ReactNode
}

function stripTabLabel(items: any[]): any[] {
  return items.map(item => {
    const { tabLabel, ...rest } = item
    if (item.children) {
      return { ...rest, children: stripTabLabel(item.children) }
    }
    return rest
  })
}

const Layout: React.FC<LayoutProps> = observer(({ children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const authStore = useAuthStore()
  const tabStore = useTabStore()
  const layoutStore = useLayoutStore()
  const {
    token: { colorBgContainer },
  } = theme.useToken()

  // 菜单配置，包含标签页信息
  const menuItems = [
    // {
    //   key: '/dashboard',
    //   icon: <DashboardOutlined />,
    //   label: '仪表盘',
    //   tabLabel: '仪表盘',
    // },
    {
      key: '/modules',
      icon: <AppstoreOutlined />,
      label: '模块管理',
      children: [
        {
          key: '/modules/list',
          label: '模块列表',
          tabLabel: '模块列表',
        },
        {
          key: '/modules/phase',
          label: '项目阶段',
          tabLabel: '项目阶段',
        },
      ],
    },
    {
      key: '/firmware',
      icon: <CloudUploadOutlined />,
      label: '小包管理',
      children: [
        {
          key: '/firmware/list',
          label: '小包列表',
          tabLabel: '小包列表',
        },
        // {
        //   key: '/firmware/ota',
        //   label: 'OTA升级',
        //   tabLabel: 'OTA升级',
        // },
      ],
    },
    {
      key: '/campaigns',
      icon: <RocketOutlined />,
      label: 'Campaign管理',
      children: [
        {
          key: '/campaigns/list',
          label: 'Campaign列表',
          tabLabel: 'Campaign列表',
        },
        {
          key: '/campaigns/force-upgrade',
          label: '车辆强制升级',
          tabLabel: '车辆强制升级',
        },
      ],
    },
    {
      key: '/devices',
      icon: <MobileOutlined />,
      label: '设备管理',
      children: [
        {
          key: '/devices/list',
          label: '设备列表',
          tabLabel: '设备列表',
        },
      ],
    },
    {
      key: '/logs',
      icon: <FileTextOutlined />,
      label: '日志管理',
      children: [
        {
          key: '/logs/list',
          label: '日志列表',
          tabLabel: '日志列表',
        },
      ],
    },
  ]

  // 扁平化菜单项，用于查找标签信息
  const flatMenuItems = useMemo(() => {
    const items: any[] = []
    const flatten = (menuList: any[]) => {
      menuList.forEach(item => {
        if (item.children) {
          flatten(item.children)
        } else {
          items.push(item)
        }
      })
    }
    flatten(menuItems)
    return items
  }, [])

  // 监听路由变化，自动添加标签页
  useEffect(() => {
    const currentPath = location.pathname
    const menuItem = flatMenuItems.find(item => item.key === currentPath)

    if (menuItem) {
      // 检查是否已经存在该tab，避免重复添加
      const existingTab = tabStore.tabs.find(tab => tab.key === currentPath)
      if (!existingTab) {
        tabStore.addTab({
          key: currentPath,
          label: menuItem.tabLabel || menuItem.label,
          path: currentPath,
          closable: currentPath !== '/firmware/list', // 固件列表不可关闭
        })
      } else {
        // 如果tab已存在，确保它是激活状态
        tabStore.setActiveKey(currentPath)
      }

      // 自动展开对应的菜单项
      const parentKey = menuItems.find(item =>
        item.children?.some(child => child.key === currentPath)
      )?.key

      if (parentKey && !layoutStore.openKeys.includes(parentKey)) {
        layoutStore.setOpenKeys([parentKey])
      }
    }
  }, [location.pathname, flatMenuItems, tabStore])

  // 初始化菜单展开状态
  useEffect(() => {
    const currentPath = location.pathname
    const parentKey = menuItems.find(item =>
      item.children?.some(child => child.key === currentPath)
    )?.key

    if (parentKey && layoutStore.openKeys.length === 0) {
      layoutStore.setOpenKeys([parentKey])
    }
  }, [location.pathname, menuItems, layoutStore.openKeys])

  // 计算当前选中的菜单项
  const selectedKeys = useMemo(() => {
    return [tabStore.activeKey || location.pathname]
  }, [location.pathname, tabStore.activeKey])

  const handleMenuClick = ({ key }: { key: string }) => {
    const menuItem = flatMenuItems.find(item => item.key === key)

    if (menuItem) {
      // 添加标签页
      tabStore.addTab({
        key,
        label: menuItem.tabLabel || menuItem.label,
        path: key,
        closable: key !== '/firmware/list',
      })
    }

    navigate(key)
  }

  const handleOpenChange = (keys: string[]) => {
    // 实现手风琴效果：一次只能展开一个父级菜单
    const latestOpenKey = keys.find(key => layoutStore.openKeys.indexOf(key) === -1)
    const rootSubmenuKeys = ['/modules', '/firmware', '/campaigns', '/devices', '/logs']

    if (rootSubmenuKeys.indexOf(latestOpenKey!) === -1) {
      layoutStore.setOpenKeys(keys)
    } else {
      layoutStore.setOpenKeys(latestOpenKey ? [latestOpenKey] : [])
    }
  }

  const handleLogout = async () => {
    await authStore.logout()
    navigate('/firmware/list', { replace: true })
  }

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ]

  return (
    <AntLayout className='layout' style={{ marginLeft: collapsed ? 80 : 200 }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        className='layout-sider'
        style={{ overflow: 'auto', height: '100vh', position: 'fixed', left: 0 }}
      >
        <div className='logo'>
          <h2>{collapsed ? 'OTA' : 'OTA管理系统'}</h2>
        </div>
        <Menu
          mode='inline'
          selectedKeys={selectedKeys}
          openKeys={layoutStore.openKeys}
          onOpenChange={handleOpenChange}
          items={stripTabLabel(menuItems)}
          onClick={handleMenuClick}
        />
      </Sider>
      <AntLayout>
        <Header className='layout-header' style={{ background: colorBgContainer }}>
          <div className='header-left'>
            <Button
              type='text'
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              className='trigger'
            />
          </div>
          <div className='header-right'>
            <Dropdown menu={{ items: userMenuItems }} placement='bottomRight'>
              <div className='user-info'>
                <Avatar icon={<UserOutlined />} />
                <span className='username'>{authStore.user?.username}</span>
              </div>
            </Dropdown>
          </div>
        </Header>

        {/* 工作流提示区域 */}
        <div
          className='workflow-tips-header'
          style={{
            background: colorBgContainer,
            padding: '8px 24px',
            borderBottom: '1px solid #f0f0f0',
            color: '#999',
            fontSize: '12px',
            lineHeight: '1.5',
          }}
        >
          工作流：1. 模块管理 - 模块列表：新增车辆模块　2. 固件管理 - 固件列表：新增固件包　3.
          Campaign管理 - Campaign列表：创建升级活动　4.
          车辆设备根据自身的模块信息确认所需固件，进行拉取
        </div>

        <TabBar />
        <Content className='layout-content'>{children}</Content>
      </AntLayout>
    </AntLayout>
  )
})

export default Layout
